import * as React from 'react';
import {
  alertClasses as muiAlertClasses,
  capitalize,
  useThemeProps,
  type AlertProps as MuiAlertProps,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { getToken } from '@workiva/unify-tokens';
import buttonClasses from 'src/components/Button/buttonClasses';
import { IAlertBaseContext } from 'src/components/AlertBase/AlertBaseContext';
import {
  AlertBase,
  type AlertBaseProps,
} from 'src/components/AlertBase/AlertBase';
import { getAlertBaseStyledOverridesResolver } from 'src/components/AlertBase/styledAlertBaseOverridesResolver';
import { getAlertBaseUtilityClass } from 'src/components/AlertBase/alertBaseClasses';
import * as UnifyIcons from 'src/components/UnifyIcons';

const defaultIconMapping = {
  success: (
    <UnifyIcons.CheckCircle
      fontSize="inherit"
      data-testid="SuccessOutlinedIcon"
    />
  ),
  warning: <UnifyIcons.Warning fontSize="inherit" />,
  error: (
    <UnifyIcons.DoNotDisturbOn
      fontSize="inherit"
      data-test-id="unify.icons.error"
    />
  ),
  info: <UnifyIcons.Info fontSize="inherit" />,
};

/**
 * Props that we do not want to include with Alert.
 *
 * Using `keyof Pick` provides type safety that ensures we are
 * only blocking props that are part of the interface.
 **/
export type AlertBlockedProps = keyof Pick<
  AlertBaseProps,
  'elevation' | 'iconMapping'
>;

/**
 * Deprecate props that aren't supported in Unify.
 */
export type UnsupportedProps = {
  /**
   * @deprecated Unsupported prop: Use the severity prop to set both color and icon instead. If you want to hide the icon, use `showIcon` instead.
   * Changing the Alert icon is not supported in Unify design guidelines and will be removed in the next major.
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   */
  icon?: MuiAlertProps['icon'];

  /**
   * @deprecated Unsupported prop: Use the severity prop to set both color and icon instead. This prop is not supported in Unify design guidelines and will be removed in the next major.
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   */
  color?: MuiAlertProps['color'];

  /**
   * @deprecated Unsupported prop: This prop is not supported in Unify design guidelines and will be removed in the next major.
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   */
  square?: MuiAlertProps['square'];

  /**
   * @deprecated Unsupported prop: This prop is no longer supported in Unify design guidelines and will be removed in the next major.
   *
   * Setting the prop will no longer change the appearance of an Alert.
   *
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   */
  variant?: MuiAlertProps['variant'];
};

export type AlertProps = Omit<AlertBaseProps, AlertBlockedProps> &
  UnsupportedProps;

const UnifyAlertRoot = styled(AlertBase, {
  name: 'UnifyAlert',
  slot: 'Root',
  overridesResolver: getAlertBaseStyledOverridesResolver('root'),
})<
  Omit<AlertProps, 'severity'> & Required<Pick<IAlertBaseContext, 'severity'>>
>(({ severity }) => {
  const severityToken = severity === 'info' ? 'primary' : severity;

  return {
    [`&.${getAlertBaseUtilityClass(`severity${capitalize(severity)}`)}`]: {
      color: getToken(`Core/Text/text-${severityToken}-onColor`),
      backgroundColor: getToken(
        `Component/Alert/alert-${severityToken}-background`
      ),
      border: `1px solid ${getToken(
        `Component/Alert/alert-${severityToken}-border`
      )}`,

      [`.${muiAlertClasses.icon}`]: {
        color: getToken(`Core/Icon/icon-${severityToken}-onColor`),
      },

      [`.${muiAlertClasses.message}`]: {
        '& a, & [role="link"]': {
          color: 'currentcolor',
          textDecoration: 'underline',
        },
      },

      [`.${muiAlertClasses.action}`]: {
        // Make sure link and icon styles match the current color.
        // NOTE: We now support AlertAction, these are only here for backwards compatibility.
        [`& a, & [role="link"], & .${buttonClasses.text}, & svg, & button:has(svg)`]:
          {
            fontWeight: 'bold',
            color: getToken(
              `Component/Alert/alert-${severityToken}-action-onColor`
            ),
            textDecoration: 'underline',
            '&:hover': {
              color: getToken(
                `Component/Alert/alert-${severityToken}-action-onColor-hover`
              ),
              textDecorationColor: getToken(
                `Component/Alert/alert-${severityToken}-action-onColor-hover`
              ),
            },
          },
      },
    },
  };
});

/**
 * An "inline" alert that should be used to communicate brief information to users
 * related to a specific section of a view.
 *
 * The messages within alerts can convey various levels of "severity" using the `severity` prop.
 *
 * **DEMOS:** <https://unify.workiva.org/components/alert?tab=code>
 */
export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  function Alert(inProps, forwardedRef) {
    const props = useThemeProps({
      props: inProps,
      name: 'MuiAlert',
    });

    const {
      children,
      severity: severityIn,
      // eslint-disable-next-line deprecation/deprecation
      color: colorIn,
      ...rest
    } = props;

    const severity =
      // Currently we don't use the value of defaultAlertBaseContextValue.severity
      // here because the Alert component was already defaulting to success.
      // While that is not what we want - its the way it has to be for now
      // until we can do a migration or major release.
      severityIn ?? colorIn ?? 'success';

    return (
      <UnifyAlertRoot
        ref={forwardedRef}
        // elevation is not supported for inline Alerts
        elevation={0}
        severity={severity}
        iconMapping={defaultIconMapping}
        {...rest}
      >
        {children}
      </UnifyAlertRoot>
    );
  }
);

export default Alert;
